import { Dispatch, FormEvent, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { FALLBACK_PROJECT_DATES_SCHEMA, ProjectActivitiesConstants, ProjectStatusConstants } from "../../constants";
import { parseProjectPhase, ProjectPhase } from "../../constants/project-phase";
import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { CursorChangeProps, ProjectDates, ProjectDatesData, ProjectPhaseStatus, ResultType } from "../../models";
import { ProjectDatesSchema } from "../../models/project/ProjectDatesSchema";
import { updateProjectDetails, updateProjectStatus } from "../../service/project";
import {
  getStandardSpecificSchemaDetails,
  searchActivities,
  searchWorkflowTasks,
  searchWorkflowTasksOutsidePhaseDates,
  SearchWorkflowTasksResponse,
} from "../../service/query";
import { PromiseAllSettledStatus, ResultData, ServiceError, Status } from "../../service/Shared";
import { updateWorkflowTask } from "../../service/workflow";
import { useAuth } from "../../useAuth";
import {
  convertDateToStringYYYYMMDD,
  flattenObject,
  getProjectDateValidationErrorFieldKeys,
  hasProjectDateValidationError,
  jsonReviver,
  recursivelyValidateAndTransformDateToString,
  setValueFromFlattenedKey,
  useIsLoadingWrapper,
} from "../../utils";
import { getWorkflowTemplatesRoute } from "../../utils/routes";
import { ButtonVariant, DataGridColumnDefinition, dataGridMapFilterCriteria, Toast } from "../../widget";
import { InputCellFormatterData } from "../../widget/data/DataGrid/models";
import { getCurrentPhaseStatus } from "./utils";
import { isDeadlineOutsidePhase } from "./utils/isDeadlineOutsidePhase";

interface UseEditDatesPanelProps {
  data: ProjectDatesData;
  show: boolean;
  setData: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
  onClose: () => void;
  initialPhase?: ProjectPhase;
  shouldRefreshProjectDetails?: () => void;
}

interface UseEditDatesPanelReturnData {
  dateSchema: ProjectDatesSchema | undefined;
  dates: ProjectDates;
  errors: ServiceError[] | undefined;
  hasDateError: (key: string) => boolean;
  isHandleSubmitLoading: boolean;
  hasMilestones: boolean;
  phase: ProjectPhase;
  addedCycleCount: number;
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  isLoadingMilestones: boolean;
  refreshMilestones: boolean;
  buttonType: ButtonVariant | undefined;
  onChange: ({ filtering, sorting, paging }: CursorChangeProps) => Promise<ResultType>;
  handlePhaseChange: (phase: ProjectPhase) => void;
  handleToggleCompletePhase: (isPhaseComplete: boolean) => void;
  handleClosePanel: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleUpdateProjectDates: (date: Date | number | undefined, key: string) => void;
  handleAddCycle: () => void;
  handleRemoveCycle: (cycleUuid: string) => void;
  handleNavigateToWorkflowTemplates: () => void;
  isCurrentPhaseComplete: boolean;
  shouldShowCompletePhaseToggle: boolean;
}

const PAGE_SIZE = 5;

const columns: DataGridColumnDefinition[] = [
  {
    name: "Milestone task",
    key: "name",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 400,
    filterable: true,
    sortable: true,
  },
  {
    name: "Assigned to",
    key: "assignee.name",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 140,
    filterable: true,
    sortable: true,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 94,
    filterable: true,
    sortable: true,
  },
  {
    name: "Deadline",
    key: "deadlineDateInput",
    dataType: "Date",
    formatter: "input",
    minWidth: 140,
    filterable: false,
    sortable: true,
  },
];

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];

const phaseStatusPathMap: Record<ProjectPhase, string> = {
  [ProjectPhase.feasibility]: "feasibility.status",
  [ProjectPhase.design]: "design.status",
  [ProjectPhase.implementation]: "implementation.status",
  [ProjectPhase.ongoingMonitoring]: "ongoingMonitoring.overallStatus",
};

const nextPhaseMap: Partial<Record<ProjectPhase, ProjectPhase>> = {
  [ProjectPhase.feasibility]: ProjectPhase.design,
  [ProjectPhase.design]: ProjectPhase.implementation,
  [ProjectPhase.implementation]: ProjectPhase.ongoingMonitoring,
};

const UPDATE_UNDER_DEVELOPMENT_STATUSES = [
  ProjectPhase.design,
  ProjectPhase.implementation,
  ProjectPhase.ongoingMonitoring,
];

const INACTIVE_ASSOCIATED_ACTIVITIES = [
  ProjectActivitiesConstants.STATUS_APPROVED,
  ProjectActivitiesConstants.STATUS_WITHDRAWN,
  ProjectActivitiesConstants.STATUS_REJECTED,
  ProjectActivitiesConstants.STATUS_SUPERSEDED,
];

const getInProgressPhase = (projectDates: ProjectDates, fallbackPhase: ProjectPhase): ProjectPhase =>
  (Object.values(ProjectPhase).find(
    (projectPhase) => getCurrentPhaseStatus(projectPhase, projectDates) === ProjectPhaseStatus.IN_PROGRESS
  ) as ProjectPhase | undefined) ?? fallbackPhase;

const getResolvedProjectPhase = (value: string | null | undefined): ProjectPhase =>
  parseProjectPhase(value) ?? ProjectPhase.feasibility;

export const useEditDatesPanel = ({
  data,
  show,
  setData,
  onClose,
  initialPhase,
  shouldRefreshProjectDetails,
}: UseEditDatesPanelProps): UseEditDatesPanelReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();

  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [hasMilestones, setHasMilestones] = useState(false);

  // Use this to determine whether we can remove a cycle (if it was just created by user)
  const [addedCycleCount, setAddedCycleCount] = useState(0);

  const [dateSchema, setDateSchema] = useState<ProjectDatesSchema>();

  const [dates, setDates] = useState<ProjectDates>(data.dates);
  const openingPhase = initialPhase ?? getResolvedProjectPhase(data.cachedPhase);
  const [phase, setPhase] = useState<ProjectPhase>(openingPhase);
  const [buttonType, setButtonType] = useState<ButtonVariant>();
  const [shouldUpdateProjectStatusToUnderDevelopment, setShouldUpdateProjectStatusToUnderDevelopment] =
    useState<boolean>(false);
  const [hasAssociatedActiveActivity, setHasAssociatedActiveActivity] = useState<boolean | undefined>();

  const [milestonesWithUpdatedDates, setMilestonesWithUpdatedDates] = useState<SearchWorkflowTasksResponse["results"]>(
    []
  );

  const handleNavigateToWorkflowTemplates = (): void => {
    navigate(getWorkflowTemplatesRoute(currentUserType));
  };

  const handlePhaseChange = (newPhase: ProjectPhase): void => {
    setPhase(newPhase);
  };

  const isCurrentPhaseComplete = useMemo(
    () => getCurrentPhaseStatus(phase, dates) === ProjectPhaseStatus.COMPLETED,
    [phase, dates]
  );
  const shouldShowCompletePhaseToggle = useMemo(() => {
    return (
      !hasAssociatedActiveActivity &&
      (getCurrentPhaseStatus(phase, dates) === ProjectPhaseStatus.IN_PROGRESS ||
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        getCurrentPhaseStatus(nextPhaseMap[phase]!, dates) === ProjectPhaseStatus.IN_PROGRESS ||
        (getCurrentPhaseStatus(phase, dates) === ProjectPhaseStatus.COMPLETED &&
          phase === ProjectPhase.ongoingMonitoring))
    );
  }, [phase, dates, hasAssociatedActiveActivity]);

  const handleSetShouldUpdateProjectStatus = (isPhaseComplete: boolean): void => {
    const shouldUpdateProjectStatus =
      isPhaseComplete &&
      UPDATE_UNDER_DEVELOPMENT_STATUSES.includes(phase) &&
      (data.currentStatus === ProjectStatusConstants.DRAFT ||
        data.currentStatus === ProjectStatusConstants.PRE_DEVELOPMENT);

    setShouldUpdateProjectStatusToUnderDevelopment(shouldUpdateProjectStatus);
  };

  const handleToggleCompletePhase = (isPhaseComplete: boolean): void => {
    const orderedPhases = Object.values(ProjectPhase);
    const selectedPhaseIndex = orderedPhases.findIndex((orderedPhase) => orderedPhase === phase);

    if (selectedPhaseIndex < 0) {
      return;
    }

    setDates((previousDates) => {
      let updatedDates = previousDates;

      orderedPhases.forEach((orderedPhase, orderedPhaseIndex) => {
        let targetStatus = ProjectPhaseStatus.NOT_STARTED;

        if (isPhaseComplete) {
          if (orderedPhaseIndex <= selectedPhaseIndex) {
            targetStatus = ProjectPhaseStatus.COMPLETED;
          } else if (orderedPhaseIndex === selectedPhaseIndex + 1) {
            targetStatus = ProjectPhaseStatus.IN_PROGRESS;
          }
        } else if (orderedPhaseIndex < selectedPhaseIndex) {
          targetStatus = ProjectPhaseStatus.COMPLETED;
        } else if (orderedPhaseIndex === selectedPhaseIndex) {
          targetStatus = ProjectPhaseStatus.IN_PROGRESS;
        }

        updatedDates = setValueFromFlattenedKey(phaseStatusPathMap[orderedPhase], targetStatus, updatedDates);
      });

      return updatedDates;
    });

    handleSetShouldUpdateProjectStatus(isPhaseComplete);
  };

  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState<boolean>(false);
  const [isLoadingMilestones, setIsLoadingMilestones] = useState<boolean>(false);
  const [refreshMilestones, setRefreshMilestones] = useState<boolean>(false);
  const errorFieldKeys = useMemo(() => getProjectDateValidationErrorFieldKeys(errors), [errors]);

  const handleClosePanel = (): void => {
    setErrors(undefined);
    setDates(data.dates); // Reset dates to the original
    setPhase(openingPhase);
    setShouldUpdateProjectStatusToUnderDevelopment(false);
    setAddedCycleCount(0);
    setMilestonesWithUpdatedDates([]);
    onClose();
  };

  const handleUpdateProjectDates = (date: Date | number | undefined, key: string): void => {
    setDates((prevDates) => setValueFromFlattenedKey(key, date, prevDates));
  };

  const handleSearchMilestonesOutsidePhases = async (): Promise<ServiceError[] | undefined> => {
    if (
      !dates.feasibility.start ||
      !dates.feasibility.end ||
      !dates.design.start ||
      !dates.design.end ||
      !dates.implementation.start ||
      !dates.implementation.end ||
      !dates.ongoingMonitoring.overallStart ||
      !dates.ongoingMonitoring.overallEnd
    ) {
      return undefined;
    }

    const res = await searchWorkflowTasksOutsidePhaseDates({
      objectUuid: data.uuid,
      feasibilityStart: dates.feasibility.start,
      feasibilityEnd: dates.feasibility.end,
      designStart: dates.design.start,
      designEnd: dates.design.end,
      implementationStart: dates.implementation.start,
      implementationEnd: dates.implementation.end,
      ongoingMonitoringOverallStart: dates.ongoingMonitoring.overallStart,
      ongoingMonitoringOverallEnd: dates.ongoingMonitoring.overallEnd,
    });

    const milestoneErrors: ServiceError[] = [];
    if (res.status === Status.Success && res.data && res.data.results.length > 0) {
      // Check if any milestones in db have a deadline outside of the current phase dates
      res.data.results.forEach((m) => {
        const deadlineDate = milestonesWithUpdatedDates.find((m2) => m2.uuid === m.uuid)?.deadlineDate;
        const milestoneError = isDeadlineOutsidePhase(m, dates, deadlineDate ?? undefined);

        if (milestoneError) {
          milestoneErrors.push(milestoneError);
        }
      });
    }
    // Check whether any updated milestones have a deadline outside of the current phase dates
    milestonesWithUpdatedDates
      .filter((m) => !res.data?.results.some((m2) => m2.uuid === m.uuid))
      .forEach((m) => {
        const deadlineDate = milestonesWithUpdatedDates.find((m2) => m2.uuid === m.uuid)?.deadlineDate;
        const milestoneError = isDeadlineOutsidePhase(m, dates, deadlineDate ?? undefined);

        if (milestoneError) {
          milestoneErrors.push(milestoneError);
        }
      });
    return milestoneErrors.length > 0 ? milestoneErrors : undefined;
  };

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors(undefined);

    const milestoneErrors = await handleSearchMilestonesOutsidePhases();
    if (milestoneErrors) {
      setErrors(milestoneErrors);
      return;
    }

    const datesDeepCopy = JSON.parse(JSON.stringify(dates), jsonReviver) as ProjectDates;
    const transformedDates = recursivelyValidateAndTransformDateToString(datesDeepCopy);

    if (transformedDates.errors.length > 0) {
      setErrors(transformedDates.errors);
      return;
    }

    const updateProjectDetailsRes = await updateProjectDetails({
      projectUuid: data.uuid,
      dates: JSON.stringify(transformedDates.data),
      rowVersion: data.rowVersion,
      addressCountryCode: null,
      displayName: null,
      locationCoordinates: null,
      locationGridReference: null,
      areaNetHa: null,
      validatorUuid: null,
      externalReference: null,
      name: null,
      addressRegion: null,
    });

    if (updateProjectDetailsRes.status === Status.Success && updateProjectDetailsRes.data) {
      setDates(datesDeepCopy);
      const inProgressPhase = getInProgressPhase(datesDeepCopy, phase);
      let updatedProjectRowVersion = updateProjectDetailsRes.data.rowVersion;
      let updatedProjectStatus = data.currentStatus;

      // If this isn't performant enough, we need to allow array of tasks in updateWorkflowTask
      const results = await Promise.allSettled(
        milestonesWithUpdatedDates.map((task) =>
          updateWorkflowTask({
            assignedToUserUuid: task.assignee?.uuid ?? null,
            deadlineDate: task.deadlineDate,
            description: task.description,
            isMandatory: task.isMandatory,
            isMilestone: task.isMilestone,
            keyDate: task.keyDate,
            name: task.name ?? "",
            objectType: task.objectType,
            objectUuid: task.objectUuid,
            reminderDate: null,
            reminderType: null,
            repeat: task.repeat ?? false,
            visibility: [],
            ownerOrganisationUuid: task.ownerOrganisation.uuid ?? "",
            rowVersion: task.rowVersion ?? 1,
            resolution: null,
            status: task.status,
            workflowTaskUuid: task.uuid ?? "",
            workflowStepUuid: task.step?.uuid ?? "",
          })
        )
      );

      const updateWorkflowTaskErrors: ServiceError[] = [];
      const successfullyUpdatedMilestones: SearchWorkflowTasksResponse["results"] = [];
      // Handle results
      results.forEach((result, index) => {
        const taskName = milestonesWithUpdatedDates[index].name ?? `Task ${index + 1}`;
        if (result.status === PromiseAllSettledStatus.Fullfilled) {
          const res = result.value;
          if (res.status === Status.Success) {
            successfullyUpdatedMilestones.push(milestonesWithUpdatedDates[index]);
            // If the task was updated successfully, we can remove it from the milestonesWithUpdatedDates
            setMilestonesWithUpdatedDates((prev) =>
              prev.filter((m) => m.uuid !== milestonesWithUpdatedDates[index].uuid)
            );
          }
          if (res.status === Status.Error && res.errors) {
            updateWorkflowTaskErrors.push({
              code: "UPDATE_WORKFLOW_TASK_FAILED",
              message: `Failed to update ${taskName}, please solve the issues below:`,
            });
            res.errors?.forEach((error) => updateWorkflowTaskErrors.push(error));
          }
        }

        if (result.status === PromiseAllSettledStatus.Rejected) {
          updateWorkflowTaskErrors.push({
            code: "UPDATE_WORKFLOW_TASK_FAILED",
            message: `Failed to update task "${taskName}". Please try again`,
          });
        }
      });

      // Update milestone data on the roadmap
      const updatedDataMilestones = data.milestones?.map((m) => ({
        ...m,
        deadlineDate: successfullyUpdatedMilestones.find((u) => u.uuid === m.uuid)?.deadlineDate ?? m.deadlineDate,
      }));

      let shouldRefresh = false;
      if (shouldUpdateProjectStatusToUnderDevelopment) {
        const updateProjectStatusRes = await updateProjectStatus({
          projectUuid: data.uuid,
          rowVersion: updatedProjectRowVersion,
          status: ProjectStatusConstants.UNDER_DEVELOPMENT,
        });

        if (updateProjectStatusRes.status === Status.Success && updateProjectStatusRes.data) {
          updatedProjectRowVersion = updateProjectStatusRes.data.rowVersion;
          updatedProjectStatus = ProjectStatusConstants.UNDER_DEVELOPMENT;
          setShouldUpdateProjectStatusToUnderDevelopment(false);
          shouldRefresh = true;
        }

        if (updateProjectStatusRes.status === Status.Error) {
          updateWorkflowTaskErrors.push(...(updateProjectStatusRes.errors ?? []));
        }
      }

      setData({
        ...data,
        cachedPhase: inProgressPhase,
        milestones: updatedDataMilestones,
        dates: datesDeepCopy,
        currentStatus: updatedProjectStatus,
        rowVersion: updatedProjectRowVersion,
      });

      if (shouldRefresh && shouldRefreshProjectDetails !== undefined) {
        shouldRefreshProjectDetails();
      }

      if (updateWorkflowTaskErrors.length > 0) {
        setErrors(updateWorkflowTaskErrors);
        return;
      }

      Toast.success({ message: "Project dates successfully updated" });
      onClose();
    }
    if (updateProjectDetailsRes.status === Status.Error) {
      setErrors(updateProjectDetailsRes.errors);
    }
  }, setIsHandleSubmitLoading);

  const handleAddCycle = (): void => {
    const cyclesLength = dates.ongoingMonitoring.cycles.length;
    const newCycle = {
      uuid: uuidv4(),
      sequence: cyclesLength + 1,
    };
    const updatedDates = setValueFromFlattenedKey(
      `ongoingMonitoring.cycles[${cyclesLength}]`,
      newCycle,
      dates
    ) as ProjectDates;
    setDates(updatedDates);
    setAddedCycleCount(cyclesLength === 0 ? 0 : addedCycleCount + 1);
  };

  const handleRemoveCycle = (cycleUuid: string): void => {
    setDates({
      ...dates,
      ongoingMonitoring: {
        ...dates.ongoingMonitoring,
        cycles: dates.ongoingMonitoring.cycles.filter((cycle) => cycle.uuid !== cycleUuid),
      },
    });
    setAddedCycleCount(addedCycleCount - 1);
  };

  const formatData = (responseData: SearchWorkflowTasksResponse): ResultData[] =>
    responseData?.results?.map((el) => {
      const result = flattenObject(el);

      result.deadlineDateInput = <InputCellFormatterData>{
        name: `deadlineDateInput-${el.uuid}`,
        type: "date",
        value: convertDateToStringYYYYMMDD(
          milestonesWithUpdatedDates.find((m) => m.uuid === el.uuid)?.deadlineDate ?? el.deadlineDate
        ),
        onChange: (value) => {
          const deadlineDate = new Date(value);

          if (Number.isNaN(deadlineDate.getTime())) {
            return;
          }
          if (milestonesWithUpdatedDates.some((m) => m.uuid === el.uuid)) {
            setMilestonesWithUpdatedDates((prev) =>
              prev.map((m) => (m.uuid === el.uuid ? { ...el, deadlineDate } : m))
            );
          } else {
            setMilestonesWithUpdatedDates((prev) => [
              ...prev,
              {
                ...el,
                deadlineDate,
              },
            ]);
          }
        },
      };

      return result;
    }) || [];

  const onChange = async ({ filtering, sorting, paging }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);
    setIsLoadingMilestones(true);

    if (phase === ProjectPhase.ongoingMonitoring) {
      setButtonType("primary");
    } else {
      setButtonType("tertiary");
    }

    let milestonesData: ResultType = {
      resultData: [],
      paging: {
        pageSize: PAGE_SIZE,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    filterCriteria.step = {
      phase: {
        operator: "eq",
        value: phase,
      },
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    filterCriteria.isMilestone = {
      operator: "eq",
      value: true,
    };

    filterCriteria.workflowObjectUuid = {
      operator: "eq",
      value: data.uuid,
    };

    await searchWorkflowTasks({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: PAGE_SIZE,
      },
      sort: sorting.map((sort) => ({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        key: sort.key as any,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        if (response.status === Status.Success && response.data) {
          milestonesData = {
            resultData: formatData(response.data),
            paging: {
              startCursor: response.data?.paging?.startCursor || "",
              endCursor: response.data?.paging?.endCursor || "",
              pageSize: PAGE_SIZE,
              totalCount: response.data?.paging?.total || 0,
              hasNextPage: response.data?.paging?.hasNextPage || false,
              hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
            },
          };
        }
      })
      .finally(() => {
        setIsLoadingMilestones(false);
      });

    setHasMilestones(milestonesData.resultData.length > 0);

    return milestonesData;
  };

  // Fetches the project's standard-specific date schema and stores it for the panel to render from.
  // The backend returns the schema as a JSON string whose top-level `properties` object is the
  // per-phase shape we care about (feasibility/design/implementation/duration/ongoingMonitoring), so
  // we parse it and lift `.properties` out. If the project has no schema this is never called and
  // `dateSchema` stays undefined, causing the fallback to be used below.
  const loadProjectDatesSchema = useCallback(
    async (uuid: string): Promise<void> => {
      const result = await getStandardSpecificSchemaDetails({ standardSpecificSchemaUuid: uuid });
      if (result.status === Status.Success && result.data) {
        setDateSchema(JSON.parse(result.data.dateSchema).properties as ProjectDatesSchema);
      }
    },
    [data.standardSpecificSchemaUuid]
  );

  const loadHasAssociatedActiveActivity = useCallback(
    async (uuid: string): Promise<void> => {
      const result = await searchActivities({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 10,
        },
        /* eslint-enable */
        filter: { results: { project: { uuid: { operator: "eq", value: uuid } } } },
      });

      if (result.status === Status.Success && result.data) {
        setHasAssociatedActiveActivity(
          result.data.results.some((x) => !INACTIVE_ASSOCIATED_ACTIVITIES.includes(x.status))
        );
      }
    },
    [data.uuid]
  );

  // Update dates when uuid changes
  useEffect(() => {
    setDates(data.dates);
    setShouldUpdateProjectStatusToUnderDevelopment(false);

    loadHasAssociatedActiveActivity(data.uuid);

    // Only projects on a standard carry a schema; schema-less projects skip this and fall back below.
    if (data.standardSpecificSchemaUuid) loadProjectDatesSchema(data.standardSpecificSchemaUuid);

    // Reset addedCycleCount when the project changes
    setAddedCycleCount(0);

    // If there are no cycles present, add one
    if (data.dates.ongoingMonitoring.cycles.length === 0) {
      handleAddCycle();
    }
  }, [data.uuid]);

  useEffect(() => {
    setPhase(openingPhase);
  }, [openingPhase, show]);

  useEffect(() => {
    setRefreshMilestones((prev) => !prev);
  }, [phase, milestonesWithUpdatedDates]);

  return {
    // Fall back to the core phase dates when a project has no standard-specific schema, so those
    // controls stay editable rather than disappearing entirely.
    dateSchema: dateSchema ?? FALLBACK_PROJECT_DATES_SCHEMA,
    dates,
    errors,
    hasDateError: (key: string) => hasProjectDateValidationError(errorFieldKeys, key),
    isHandleSubmitLoading,
    phase,
    addedCycleCount,
    columns,
    isLoadingMilestones,
    hasMilestones,
    defaultSortingCriteria,
    refreshMilestones,
    buttonType,
    onChange,
    handlePhaseChange,
    handleToggleCompletePhase,
    handleClosePanel,
    handleSubmit,
    handleUpdateProjectDates,
    handleAddCycle,
    handleRemoveCycle,
    handleNavigateToWorkflowTemplates,
    isCurrentPhaseComplete,
    shouldShowCompletePhaseToggle,
  };
};
